import { AxiosError } from 'axios';
import { api } from '@shared/api';
import { readAttribution } from '@shared/lib/attribution';
import type { User } from '@shared/types';
import { profileLanguage, type UiLang } from '../lib/interfaceLanguage';
import { AuthError, invalidField, mapHttpError } from './authErrors';

export { AuthError };

export interface AuthResult {
  user: User;
  accessToken: string;
}

const nameToInitials = (source: string): string =>
  source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '·';

const idFromAccessToken = (token: string): string => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return '';
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.sub === 'string' ? decoded.sub : '';
  } catch {
    return '';
  }
};

export async function signIn(payload: { email: string; password: string }): Promise<AuthResult> {
  try {
    const { data } = await api.post<{ token: string }>('/auth/login', payload);
    const accessToken = data.token;
    return {
      accessToken,
      user: {
        id: idFromAccessToken(accessToken),
        name: '',
        email: payload.email,
        initials: nameToInitials(payload.email),
        emailVerified: false,
      },
    };
  } catch (err) {
    throw mapHttpError(err, 'INVALID_CREDENTIALS');
  }
}

/**
 * Регистрация. В теле — `attribution` (B): откуда пришёл ученик, snake_case, все поля необязательны;
 * старый бэкенд лишнее поле игнорирует, новый сам обрезает значения. С 2026-10-20 там же
 * `language_interface` — язык интерфейса при регистрации (B4): по нему бэкенд может сразу создать
 * профиль на этом языке; пока не умеет — язык допишет в профиль AppGate (useInterfaceLanguageSync).
 * Если запрос с необязательными полями всё же отвергнут (422), повторяем без них: они не должны
 * мешать регистрации. Повтор безопасен — при 422 аккаунт не создаётся. Когда сервер прямо назвал почту,
 * пароль или имя, не повторяем: ответ будет тем же.
 */
export async function signUp(payload: {
  name?: string;
  email: string;
  password: string;
  language?: UiLang;
}): Promise<AuthResult> {
  const { language, name: rawName, ...credentials } = payload;
  const name = rawName?.trim() ?? '';
  // Пустое имя не шлём: сервер отверг бы его (422), без поля — пишет пустое.
  const account = name ? { ...credentials, name } : credentials;
  const attribution = readAttribution();
  const extras = {
    ...(language ? { language_interface: profileLanguage(language) } : {}),
    ...(attribution ? { attribution } : {}),
  };
  const hasExtras = Object.keys(extras).length > 0;
  const register = (withExtras: boolean) =>
    api.post<{ token: string }>('/auth/registration', withExtras ? { ...account, ...extras } : account);
  try {
    const response = await register(true).catch((err: unknown) => {
      if (!hasExtras || !(err instanceof AxiosError) || err.response?.status !== 422) throw err;
      const field = invalidField(err);
      if (field && field !== 'extras') throw err;
      return register(false);
    });
    const accessToken = response.data.token;
    return {
      accessToken,
      user: {
        id: idFromAccessToken(accessToken),
        name,
        email: account.email,
        initials: nameToInitials(name || account.email),
        emailVerified: false,
      },
    };
  } catch (err) {
    throw mapHttpError(err, 'EMAIL_EXISTS');
  }
}

export async function signOut(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    // Logout is best-effort: even if backend fails we still clear local state.
  }
}
